import React, { memo, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { ProgressIndicator } from '@segmently/schema-models/v2';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import styles from './DashesPageIndicator.module.css';

const DEFAULT_DASH_HEIGHT = 5;
const DEFAULT_DASHES_SPACING = 7;
const DEFAULT_FILLED_COLOR = '#12777f';
const DEFAULT_NOT_FILLED_COLOR = '#f2f2f2';

export interface DashesPageIndicatorProps {
  indicator: ProgressIndicator | undefined;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
}

/**
 * Calculate fill percentages for each dash based on progress value.
 */
function calculateDashFills(value: number, numberOfDashes: number): number[] {
  const clampedValue = Math.max(0, Math.min(100, value));
  const progressInDashes = (clampedValue / 100) * numberOfDashes;
  const fullDashes = Math.floor(progressInDashes);
  const partialFill = progressInDashes % 1;

  const fills: number[] = [];
  for (let i = 0; i < numberOfDashes; i++) {
    if (i < fullDashes) {
      fills.push(100);
    } else if (i === fullDashes) {
      fills.push(partialFill * 100);
    } else {
      fills.push(0);
    }
  }

  return fills;
}

/**
 * DashesPageIndicator — renders ONLY the dashes visualization.
 * Image and title are universal decorations rendered by HeaderBar.
 */
export const DashesPageIndicator = memo(function DashesPageIndicator({
  indicator,
  className,
  'data-testid': testId,
  editPath,
}: DashesPageIndicatorProps): React.ReactElement | null {
  // ⚠️ Rules of Hooks: the null-indicator early-return lives BELOW the hook —
  // an indicator can materialize on a mounted component (theme cascade writes
  // activeColor onto an empty header) and a pre-hook return flips hook count.
  // Handle raw number (legacy) or missing value (setNestedValue corruption)
  const rawValue = typeof indicator === 'number' ? indicator : (indicator as any)?.value;
  const value = typeof rawValue === 'number' ? rawValue : 0;
  const numberOfDashes = typeof indicator === 'number' ? 3 : ((indicator as any)?.numberOfDashes ?? 3);
  const numOption1 = typeof indicator === 'number' ? undefined : (indicator as any)?.numOption1;
  const numOption2 = typeof indicator === 'number' ? undefined : (indicator as any)?.numOption2;
  const strOption1 = typeof indicator === 'number' ? undefined : (indicator as any)?.strOption1;
  const strOption2 = typeof indicator === 'number' ? undefined : (indicator as any)?.strOption2;
  // v2.13.0: flat fields with fallback to old dashesAppearance/appearance for existing documents
  const ind = typeof indicator === 'number' ? undefined : (indicator as any);
  const legacyAppearance = ind?.dashesAppearance ?? ind?.appearance;

  const dashFills = useMemo(
    () => calculateDashFills(value, numberOfDashes),
    [value, numberOfDashes]
  );

  if (!indicator) {
    return null;
  }

  const dashHeight = ind?.dashHeight ?? legacyAppearance?.dashHeight ?? numOption1 ?? DEFAULT_DASH_HEIGHT;
  const dashesSpacing = ind?.dashesSpacing ?? legacyAppearance?.dashesSpacing ?? numOption2 ?? DEFAULT_DASHES_SPACING;
  const filledColor = ind?.activeColor ?? legacyAppearance?.filledColor ?? strOption1 ?? DEFAULT_FILLED_COLOR;
  const notFilledColor = ind?.trackColor ?? legacyAppearance?.notFilledColor ?? strOption2 ?? DEFAULT_NOT_FILLED_COLOR;

  const containerClassName = className
    ? `${styles.container} ${className}`
    : styles.container;

  const dashesContainerStyle: CSSProperties = {
    gap: `${dashesSpacing}px`,
  };

  const getDashStyle = (): CSSProperties => ({
    height: `${dashHeight}px`,
    backgroundColor: notFilledColor,
  });

  const getDashFillStyle = (fillPercent: number): CSSProperties => ({
    width: `${fillPercent}%`,
    backgroundColor: filledColor,
  });

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  return (
    <div
      className={containerClassName}
      data-testid={schemaTestId}
      data-edit-path={editPath}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={styles.dashesContainer}
        style={dashesContainerStyle}
        data-testid={schemaTestId ? `${schemaTestId}.dashes` : undefined}
      >
        {dashFills.map((fillPercent, index) => (
          <div
            key={index}
            className={styles.dash}
            style={getDashStyle()}
            data-testid={schemaTestId ? `${schemaTestId}.dash.${index}` : undefined}
          >
            {fillPercent > 0 && (
              <div
                className={styles.dashFill}
                style={getDashFillStyle(fillPercent)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

DashesPageIndicator.displayName = 'DashesPageIndicator';
